// src/components/groupqm/groupTokens.ts — palette + helpers for the Group
// QuickMessage net-new surfaces (Home banner, thread view, compose sheet).
//
// Decision 3 (PHASE_8_ADDENDUM): the prototype is authoritative for these
// net-new surfaces, so the heather shades are the literal hex from
// docs/build-plan/sign-in-system/slices/theme.jsx (not the runtime-themed C.*
// vars used by existing chrome). Colour lanes are strict: heather #7A5D8C is
// Group QM only — never the royal-blue 1:1 Quick Message lane (#3D6FA8), and
// never sage (duty).

import { F } from "../../theme";

export const G = {
  page: "#FBF6E8",
  pageWarm: "#F5EBD3",
  card: "#FFFFFF",
  cardSoft: "#FFFDF6",
  ink: "#2B2014",
  inkSoft: "#604C32",
  inkMute: "#9F8B6B",
  hairline: "#E8DCBC",
  hairlineLt: "#F0E7D0",

  brass: "#A6771C",
  brassDk: "#7A540B",

  // heather — the Group QM lane
  grp: "#7A5D8C",
  grpDk: "#56406A",
  grpLt: "#C4B4D2",
  grpBg: "#EBE0F0",
  grpSurf: "#F3ECF6",
} as const;

export const GF = {
  serif: F.serif,
  body: F.body,
  mono: F.mono,
} as const;

// Pulsing-dot keyframes used by the Home banner + active-segment indicator.
// Rendered once via a <style> tag inside GroupQmSurface / GroupThreadPage.
export const GRP_KEYFRAMES = `
@keyframes grpPulse { 0% { box-shadow: 0 0 0 0 rgba(122,93,140,0.5);} 70% { box-shadow: 0 0 0 7px rgba(122,93,140,0);} 100% { box-shadow: 0 0 0 0 rgba(122,93,140,0);} }
`;

export function truncate(s: string | null | undefined, n: number): string {
  if (!s) return "";
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + "…";
}

export function initialsFromName(displayName: string, fallbackCode?: string): string {
  const fromName = (displayName || "")
    .split(" ")
    .filter((s) => s && s[0] && /[A-Za-z]/.test(s[0]))
    .map((s) => s[0].toUpperCase())
    .slice(0, 2)
    .join("");
  return fromName || (fallbackCode ?? "").slice(0, 2).toUpperCase() || "?";
}

export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
}

export function describeTimeSince(iso: string | null): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const seconds = Math.floor((Date.now() - then) / 1000);
  if (seconds < 60) return "just now";
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 3) return `${hrs} hr ago`;
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
}
